import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import {
  useIndividualDetailsByClientId,
  useUpdateIndividual,
  useDeleteIndividual,
} from '../hooks/useIndividuals';
import { useContactLogs, useUpdateContactLog, useDeleteContactLog } from '../hooks/useContactLogs';
import { Individual } from '../services/individualsService';
import { ContactLog } from '../types/contactLog';
import { FNSPEligibilityStatus } from '../types/fnsp';
import { getFNSPEligibilityStatus } from '../services/fnspService';
import { FNSPEligibilityDialog } from '../components/FNSPEligibilityDialog';
import { MergeDialog } from '../components/MergeDialog';
import { ManualMergePicker } from '../components/ManualMergePicker';
import {
  DuplicateGroup,
  DuplicateIndividual,
} from '../services/duplicateService';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/auth';
import { usePrograms } from '../hooks/usePrograms';
import { CONTACT_TYPES } from '../constants/contactTypes';
import { formatDate } from '../utils/dateUtils';
import '../styles/FormPages.css';
import '../styles/ListPages.css';
import '../styles/FNSPDialog.css';

interface CANSAssessment {
  assessmentID: number;
  assessmentType: string;
  assessmentDate: string;
  hasRedFlag: boolean;
  nextDueDate: string;
  isOverdue: boolean;
}

interface CANSEligibilityResponse {
  isEligible: boolean;
  isMandatory: boolean;
  reason?: string;
  ageGroup?: string;
  age?: number;
}

export function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Individual>>({});
  const [saveError, setSaveError] = useState<string>('');

  // FNSP Eligibility state
  const [fnspEligibility, setFnspEligibility] = useState<FNSPEligibilityStatus | null>(null);
  const [fnspLoading, setFnspLoading] = useState(false);
  const [fnspDialogOpen, setFnspDialogOpen] = useState(false);

  // Track which contact log notes are expanded
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());

  // Contact log edit/delete state
  const [editingLog, setEditingLog] = useState<ContactLog | null>(null);
  const [editLogForm, setEditLogForm] = useState<Partial<ContactLog>>({});
  const [editLogError, setEditLogError] = useState('');
  const [deletingLogId, setDeletingLogId] = useState<number | null>(null);

  // Client deactivation state
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');

  // Auth context for role-based UI
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  // Single composite query using clientId (user-facing ID like 'C000031')
  const { data: detailsData, isLoading, error, refetch } = useIndividualDetailsByClientId(clientId);

  // Extract individualID for APIs that need internal ID
  const individualId = detailsData?.individual?.individualID;

  // Contact logs query (uses internal individualID)
  const { data: contactLogs, isLoading: contactLogsLoading } = useContactLogs(individualId);

  // Update mutation
  const updateMutation = useUpdateIndividual();

  // Contact log mutations
  const updateLogMutation = useUpdateContactLog();
  const deleteLogMutation = useDeleteContactLog();

  // Individual delete mutation
  const deleteMutation = useDeleteIndividual();

  // Extract data from composite response
  const individual: Individual | null = detailsData?.individual || null;
  const cansEligibility: CANSEligibilityResponse | null = detailsData?.cansEligibility || null;
  const cansAssessments: CANSAssessment[] = detailsData?.cansAssessments || [];

  // Fetch FNSP eligibility status
  const fetchFNSPEligibility = useCallback(async () => {
    if (!individualId) {
      return;
    }
    setFnspLoading(true);
    try {
      const status = await getFNSPEligibilityStatus(individualId);
      setFnspEligibility(status);
    } catch (err) {
      console.error('Error fetching FNSP eligibility:', err);
      setFnspEligibility(null);
    } finally {
      setFnspLoading(false);
    }
  }, [individualId]);

  useEffect(() => {
    if (individualId) {
      fetchFNSPEligibility();
    }
  }, [individualId, fetchFNSPEligibility]);

  const handleFNSPDialogSaved = () => {
    fetchFNSPEligibility();
    refetch(); // Refetch all data
  };

  // Loading and error handling
  const loading = isLoading;
  const errorMessage = error
    ? error instanceof Error
      ? error.message
      : 'Failed to load client'
    : '';

  const handleEditClick = () => {
    if (individual) {
      setEditForm({
        firstName: individual.firstName,
        lastName: individual.lastName,
        dateOfBirth: individual.dateOfBirth,
        gender: individual.gender,
        raceEthnicity: individual.raceEthnicity,
        address: individual.address,
        city: individual.city,
        zipCode: individual.zipCode,
        phoneNumber: individual.phoneNumber,
        emailAddress: individual.emailAddress,
      });
      setIsEditing(true);
      setSaveError('');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
    setSaveError('');
  };

  const handleSaveEdit = async () => {
    if (!individualId) {
      return;
    }

    try {
      setSaveError('');
      await updateMutation.mutateAsync({
        id: individualId,
        data: editForm,
      });
      setIsEditing(false);
      setEditForm({});
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update client information');
    }
  };

  const handleInputChange = (field: keyof Individual, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const calculateAge = (dob: string) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const age = individual ? calculateAge(individual.dateOfBirth) : 0;
  const isEligibleForFNSP = age >= 18; // FNSP eligibility is only for adults (18+)

  const toggleNoteExpanded = (contactLogID: number) => {
    setExpandedNotes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(contactLogID)) {
        newSet.delete(contactLogID);
      } else {
        newSet.add(contactLogID);
      }
      return newSet;
    });
  };

  const { programs } = usePrograms();

  const handleEditLog = (log: ContactLog) => {
    setEditingLog(log);
    setEditLogForm({
      contactDate: log.contactDate,
      durationMinutes: log.durationMinutes,
      programID: log.programID,
      summaryNotes: log.summaryNotes,
      nextFollowUpDate: log.nextFollowUpDate || '',
      isRedFlagged: log.isRedFlagged,
      isDischarge: log.isDischarge,
      isCaseReopen: log.isCaseReopen,
      contactTypes: [...log.contactTypes],
    });
    setEditLogError('');
  };

  const handleCancelEditLog = () => {
    setEditingLog(null);
    setEditLogForm({});
    setEditLogError('');
  };

  const handleSaveLog = async () => {
    if (!editingLog) {
      return;
    }
    try {
      setEditLogError('');
      await updateLogMutation.mutateAsync({
        contactLogID: editingLog.contactLogID,
        data: editLogForm,
      });
      setEditingLog(null);
      setEditLogForm({});
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('temporary') || msg.includes('Reference:')) {
        setEditLogError(msg);
      } else {
        setEditLogError(
          'Unable to save changes. Please wait a moment and try again. ' +
            'If the problem continues, contact your supervisor.'
        );
      }
    }
  };

  const handleDeleteLog = async (contactLogID: number) => {
    try {
      await deleteLogMutation.mutateAsync(contactLogID);
      setDeletingLogId(null);
    } catch (err) {
      console.error('Failed to delete contact log:', err);
    }
  };

  const handleDeactivateClient = async () => {
    if (!individualId) {
      return;
    }
    setDeactivateError('');
    try {
      await deleteMutation.mutateAsync(individualId);
      navigate('/clients');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to deactivate client.';
      setDeactivateError(message);
    }
  };

  const handleEditLogContactTypeToggle = (type: string) => {
    setEditLogForm((prev) => {
      const current = prev.contactTypes || [];
      const updated = current.includes(type)
        ? current.filter((t) => t !== type)
        : [...current, type];
      return { ...prev, contactTypes: updated };
    });
  };

  const isAdmin = hasRole([UserRole.ADMIN]);

  // Manual merge: admin picks another client to merge into this one.
  const [showMergePicker, setShowMergePicker] = useState(false);
  const [mergeGroup, setMergeGroup] = useState<DuplicateGroup<DuplicateIndividual> | null>(null);

  if (loading) {
    return <div className="loading">Loading client information...</div>;
  }

  if (errorMessage || !individual) {
    return (
      <div className="list-page">
        <div className="alert alert-error">{errorMessage || 'Client not found'}</div>
        <Link to="/clients">
          <button className="button-secondary">Back to Clients</button>
        </Link>
      </div>
    );
  }

  return (
    <div className="list-page">
      <div className="page-header">
        <h2>Client Details</h2>
        <p>
          {individual.firstName} {individual.lastName} - {individual.clientID}
        </p>
      </div>

      {/* Basic Information */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <h3>Basic Information</h3>
          {!isEditing && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="button-secondary" onClick={handleEditClick}>
                Edit Information
              </button>
              {isAdmin && !individual.mergedIntoIndividualID && (
                <button
                  className="button-secondary"
                  onClick={() => setShowMergePicker(true)}
                  title="Merge another client record into this one"
                >
                  Merge with another client…
                </button>
              )}
            </div>
          )}
        </div>

        {saveError && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {saveError}
          </div>
        )}

        {individual.mergedIntoIndividualID && (
          <div
            className="alert"
            style={{ background: '#fff8e6', border: '1px solid #FAA31B', marginBottom: '1rem' }}
          >
            <strong>This client record has been merged.</strong> Their history now lives under
            client{' '}
            <Link to={`/clients/${individual.mergedIntoIndividualID}`}>
              {individual.mergedIntoClientID || `#${individual.mergedIntoIndividualID}`}
            </Link>
            {individual.mergedAt && ` (merged ${individual.mergedAt.slice(0, 10)})`}.
          </div>
        )}

        <div
          className="client-info-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginTop: '1rem',
          }}
        >
          <div>
            <strong>Client ID:</strong> {individual.clientID}
          </div>

          {isEditing ? (
            <>
              <div>
                <strong>First Name:</strong>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.firstName || ''}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  style={{ marginTop: '0.25rem', width: '100%' }}
                  required
                />
              </div>
              <div>
                <strong>Last Name:</strong>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.lastName || ''}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  style={{ marginTop: '0.25rem', width: '100%' }}
                  required
                />
              </div>
              <div>
                <strong>Date of Birth:</strong>
                <input
                  type="date"
                  className="form-input"
                  value={editForm.dateOfBirth?.split('T')[0] || ''}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  style={{ marginTop: '0.25rem', width: '100%' }}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div>
                <strong>Gender:</strong>
                <select
                  className="form-input"
                  value={editForm.gender || ''}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  style={{ marginTop: '0.25rem', width: '100%' }}
                  required
                >
                  <option value="">Select...</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-binary">Non-binary</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>
              <div>
                <strong>Race/Ethnicity:</strong>
                <select
                  className="form-input"
                  value={editForm.raceEthnicity || ''}
                  onChange={(e) => handleInputChange('raceEthnicity', e.target.value)}
                  style={{ marginTop: '0.25rem', width: '100%' }}
                  required
                >
                  <option value="">Select...</option>
                  <option value="Hispanic or Latino">Hispanic or Latino</option>
                  <option value="Black or African American">Black or African American</option>
                  <option value="White">White</option>
                  <option value="Asian">Asian</option>
                  <option value="Native Hawaiian or Pacific Islander">
                    Native Hawaiian or Pacific Islander
                  </option>
                  <option value="American Indian or Alaska Native">
                    American Indian or Alaska Native
                  </option>
                  <option value="Two or more races">Two or more races</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div>
                <strong>Name:</strong> {individual.firstName} {individual.lastName}
              </div>
              <div>
                <strong>Date of Birth:</strong> {formatDate(individual.dateOfBirth)}
              </div>
              <div>
                <strong>Age:</strong> {age} years old
              </div>
              <div>
                <strong>Gender:</strong> {individual.gender || 'Not specified'}
              </div>
              <div>
                <strong>Race/Ethnicity:</strong> {individual.raceEthnicity || 'Not specified'}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Contact Information */}
      <div className="card">
        <h3>Contact Information</h3>
        <div
          className="client-info-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginTop: '1rem',
          }}
        >
          {isEditing ? (
            <>
              <div>
                <strong>Address:</strong>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  style={{ marginTop: '0.25rem', width: '100%' }}
                />
              </div>
              <div>
                <strong>City:</strong>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.city || ''}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  style={{ marginTop: '0.25rem', width: '100%' }}
                />
              </div>
              <div>
                <strong>Zip Code:</strong>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.zipCode || ''}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  style={{ marginTop: '0.25rem', width: '100%' }}
                  pattern="[0-9]{5}"
                  maxLength={5}
                  title="Enter a 5-digit ZIP code"
                  placeholder="12345"
                />
              </div>
              <div>
                <strong>Phone:</strong>
                <input
                  type="tel"
                  className="form-input"
                  value={editForm.phoneNumber || ''}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  style={{ marginTop: '0.25rem', width: '100%' }}
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                  title="Format: 123-456-7890"
                  placeholder="123-456-7890"
                />
              </div>
              <div>
                <strong>Email:</strong>
                <input
                  type="email"
                  className="form-input"
                  value={editForm.emailAddress || ''}
                  onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                  style={{ marginTop: '0.25rem', width: '100%' }}
                  pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                  title="Enter a valid email address"
                  placeholder="example@email.com"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <strong>Address:</strong> {individual.address || 'Not provided'}
              </div>
              <div>
                <strong>City:</strong> {individual.city || 'Not provided'}
              </div>
              <div>
                <strong>Zip Code:</strong> {individual.zipCode || 'Not provided'}
              </div>
              <div>
                <strong>Phone:</strong>{' '}
                {individual.phoneNumber ? (
                  individual.phoneNumber
                ) : (
                  <span style={{ color: '#999', fontStyle: 'italic' }}>Not Available</span>
                )}
              </div>
              <div>
                <strong>Email:</strong>{' '}
                {individual.emailAddress ? (
                  individual.emailAddress
                ) : (
                  <span style={{ color: '#999', fontStyle: 'italic' }}>Not Available</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Service Information */}
      <div className="card">
        <h3>Service Information</h3>
        <div
          className="client-info-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginTop: '1rem',
          }}
        >
          <div>
            <strong>Intake Date:</strong> {formatDate(individual.intakeDate)}
          </div>
          <div>
            <strong>Status:</strong>{' '}
            <span style={{ color: individual.isActive ? '#388E3C' : '#D32F2F', fontWeight: 600 }}>
              {individual.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* FNSP Eligibility Section - Only show for clients 18+ */}
      {isEligibleForFNSP && (
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <div>
              <h3>FNSP Eligibility</h3>
              <p style={{ color: '#666', fontSize: '0.9rem', margin: '0.5rem 0 0 0' }}>
                Family and Neighborhood Support Partnership
              </p>
            </div>
            <button
              className="button-secondary"
              onClick={() => setFnspDialogOpen(true)}
              disabled={fnspLoading}
            >
              {fnspEligibility?.hasAssessment ? 'Edit Assessment' : 'Verify Eligibility'}
            </button>
          </div>

          {fnspLoading ? (
            <p style={{ color: '#999', fontStyle: 'italic' }}>Loading eligibility status...</p>
          ) : fnspEligibility?.hasAssessment ? (
            <div style={{ marginTop: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  borderRadius: '8px',
                  backgroundColor: fnspEligibility.isEligible
                    ? 'rgba(56, 142, 60, 0.1)'
                    : 'rgba(211, 47, 47, 0.1)',
                  border: `1px solid ${fnspEligibility.isEligible ? '#388E3C' : '#D32F2F'}`,
                }}
              >
                <span
                  style={{
                    fontSize: '1.5rem',
                    color: fnspEligibility.isEligible ? '#388E3C' : '#D32F2F',
                  }}
                >
                  {fnspEligibility.isEligible ? '\u2713' : '\u2717'}
                </span>
                <div>
                  <strong style={{ color: fnspEligibility.isEligible ? '#388E3C' : '#D32F2F' }}>
                    {fnspEligibility.isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
                  </strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                    {fnspEligibility.eligibilityReason}
                  </p>
                  {fnspEligibility.assessmentDate && (
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#999' }}>
                      Last assessed: {formatDate(fnspEligibility.assessmentDate)}
                      {fnspEligibility.conductedByStaffName && (
                        <> by {fnspEligibility.conductedByStaffName}</>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#666' }}>
                <span>Risk factors: {fnspEligibility.riskFactorCount}</span>
                <span style={{ margin: '0 0.5rem' }}>|</span>
                <span>Service area: {fnspEligibility.inServiceArea ? 'Yes' : 'No'}</span>
                <span style={{ margin: '0 0.5rem' }}>|</span>
                <span>
                  Child in household: {fnspEligibility.hasChildUnder18OrDisabled21 ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          ) : (
            <div
              onClick={() => setFnspDialogOpen(true)}
              style={{
                marginTop: '1rem',
                padding: '1rem',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                border: '1px solid #FF9800',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = 'rgba(255, 152, 0, 0.2)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = 'rgba(255, 152, 0, 0.1)')
              }
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.5rem', color: '#FF9800' }}>!</span>
                <div>
                  <strong style={{ color: '#E65100' }}>Eligibility Not Verified</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                    Click here to complete the FNSP eligibility questionnaire
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CANS Assessment Section */}
      {cansEligibility && (
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <div>
              <h3>CANS Assessments</h3>
              <p style={{ color: '#666', fontSize: '0.9rem', margin: '0.5rem 0 0 0' }}>
                {cansEligibility.isEligible ? (
                  <>
                    <span
                      style={{
                        color: cansEligibility.isMandatory ? '#F44336' : '#FFC107',
                        fontWeight: 600,
                      }}
                    >
                      {cansEligibility.isMandatory ? 'Mandatory' : 'Optional'}
                    </span>{' '}
                    - {cansEligibility.reason}
                  </>
                ) : (
                  <span style={{ color: '#999' }}>{cansEligibility.reason}</span>
                )}
              </p>
            </div>
            {cansEligibility.isEligible && (
              <Link to={`/clients/${clientId}/cans/new`}>
                <button className="button-primary">New CANS Assessment</button>
              </Link>
            )}
          </div>

          {cansAssessments.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Assessment Date</th>
                    <th>Type</th>
                    <th>Next Due Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cansAssessments.map((assessment) => (
                    <tr key={assessment.assessmentID}>
                      <td>{formatDate(assessment.assessmentDate)}</td>
                      <td>{assessment.assessmentType}</td>
                      <td>{formatDate(assessment.nextDueDate)}</td>
                      <td>
                        {assessment.hasRedFlag && (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: '#F44336',
                              color: 'white',
                              marginRight: '0.5rem',
                            }}
                          >
                            Red Flag
                          </span>
                        )}
                        {assessment.isOverdue && (
                          <span
                            className="badge"
                            style={{ backgroundColor: '#FF9800', color: 'white' }}
                          >
                            Overdue
                          </span>
                        )}
                        {!assessment.hasRedFlag && !assessment.isOverdue && (
                          <span style={{ color: '#388E3C' }}>Current</span>
                        )}
                      </td>
                      <td>
                        <Link to={`/cans/${assessment.assessmentID}`}>
                          <button className="button-link">View</button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            cansEligibility.isEligible && (
              <p style={{ color: '#999', fontStyle: 'italic' }}>
                No CANS assessments on record. Click &quot;New CANS Assessment&quot; to create one.
              </p>
            )
          )}
        </div>
      )}

      {/* Service Visit History */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <div>
            <h3>Service Visit History</h3>
            <p style={{ color: '#666', fontSize: '0.9rem', margin: '0.5rem 0 0 0' }}>
              Contact logs and service visits for this client
            </p>
          </div>
          <Link to={`/clients/log-visit?clientId=${individual.clientID}`}>
            <button className="button-primary">Log Service Visit</button>
          </Link>
        </div>

        {contactLogsLoading ? (
          <p style={{ color: '#999', fontStyle: 'italic' }}>Loading service visit history...</p>
        ) : contactLogs && contactLogs.length > 0 ? (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Program</th>
                  <th>Contact Type</th>
                  <th>Duration</th>
                  <th>Staff</th>
                  <th>Notes</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contactLogs.map((log) => (
                  <tr key={log.contactLogID}>
                    <td>{formatDate(log.contactDate)}</td>
                    <td>{log.programName || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                        {log.contactTypes.map((type, idx) => (
                          <span
                            key={idx}
                            style={{
                              backgroundColor: '#E3F2FD',
                              color: '#1565C0',
                              padding: '0.15rem 0.4rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{log.durationMinutes} min</td>
                    <td>{log.staffName || '-'}</td>
                    <td style={{ maxWidth: '300px', minWidth: '200px' }}>
                      {log.summaryNotes.length > 100 ? (
                        <div>
                          <div
                            style={{
                              maxHeight: expandedNotes.has(log.contactLogID) ? '200px' : 'none',
                              overflow: expandedNotes.has(log.contactLogID) ? 'auto' : 'hidden',
                              whiteSpace: expandedNotes.has(log.contactLogID)
                                ? 'pre-wrap'
                                : 'normal',
                              wordBreak: 'break-word',
                              padding: expandedNotes.has(log.contactLogID) ? '0.5rem' : '0',
                              backgroundColor: expandedNotes.has(log.contactLogID)
                                ? '#f9f9f9'
                                : 'transparent',
                              borderRadius: expandedNotes.has(log.contactLogID) ? '4px' : '0',
                              border: expandedNotes.has(log.contactLogID)
                                ? '1px solid #e0e0e0'
                                : 'none',
                            }}
                          >
                            {expandedNotes.has(log.contactLogID)
                              ? log.summaryNotes
                              : `${log.summaryNotes.substring(0, 100)}...`}
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleNoteExpanded(log.contactLogID)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#5B2C86',
                              cursor: 'pointer',
                              padding: '0.25rem 0',
                              fontSize: '0.85rem',
                              fontWeight: 500,
                            }}
                          >
                            {expandedNotes.has(log.contactLogID) ? 'Show less' : 'Show more'}
                          </button>
                        </div>
                      ) : (
                        <span style={{ wordBreak: 'break-word' }}>{log.summaryNotes}</span>
                      )}
                    </td>
                    <td>
                      {log.isRedFlagged && (
                        <span
                          className="badge"
                          style={{ backgroundColor: '#F44336', color: 'white' }}
                        >
                          Red Flag
                        </span>
                      )}
                      {log.isDischarge && (
                        <span
                          className="badge"
                          style={{
                            backgroundColor: '#FF9800',
                            color: 'white',
                            marginLeft: log.isRedFlagged ? '0.25rem' : 0,
                          }}
                        >
                          Discharge
                        </span>
                      )}
                      {log.isCaseReopen && (
                        <span
                          className="badge"
                          style={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            marginLeft: log.isRedFlagged || log.isDischarge ? '0.25rem' : 0,
                          }}
                        >
                          Reopened
                        </span>
                      )}
                      {!log.isRedFlagged && !log.isDischarge && !log.isCaseReopen && (
                        <span style={{ color: '#666' }}>-</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          type="button"
                          onClick={() => handleEditLog(log)}
                          title="Edit"
                          style={{
                            background: 'none',
                            border: '1px solid #5B2C86',
                            color: '#5B2C86',
                            cursor: 'pointer',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                          }}
                        >
                          Edit
                        </button>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => setDeletingLogId(log.contactLogID)}
                            title="Delete (Admin)"
                            style={{
                              background: 'none',
                              border: '1px solid #F44336',
                              color: '#F44336',
                              cursor: 'pointer',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: '#999', fontStyle: 'italic' }}>
            No service visits on record. Click &quot;Log Service Visit&quot; to create one.
          </p>
        )}
      </div>

      {/* Edit Contact Log Modal */}
      {editingLog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={handleCancelEditLog}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Edit Service Visit</h3>
            {editLogError && <div className="alert alert-error">{editLogError}</div>}

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="editVisitDate">Visit Date *</label>
              <input
                type="date"
                id="editVisitDate"
                value={editLogForm.contactDate || ''}
                onChange={(e) =>
                  setEditLogForm((prev) => ({ ...prev, contactDate: e.target.value }))
                }
                max={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="editDuration">Duration (minutes) *</label>
              <input
                type="number"
                id="editDuration"
                value={editLogForm.durationMinutes ?? ''}
                onChange={(e) =>
                  setEditLogForm((prev) => ({
                    ...prev,
                    durationMinutes: parseInt(e.target.value) || 0,
                  }))
                }
                min="0"
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="editProgram">Program/Service *</label>
              <select
                id="editProgram"
                value={editLogForm.programID || ''}
                onChange={(e) =>
                  setEditLogForm((prev) => ({ ...prev, programID: parseInt(e.target.value) }))
                }
                required
              >
                <option value="">Select a program...</option>
                {programs.map((p) => (
                  <option key={p.programID} value={p.programID}>
                    {p.programName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Contact Type(s) *</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {CONTACT_TYPES.map((type) => (
                  <label
                    key={type}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                  >
                    <input
                      type="checkbox"
                      checked={(editLogForm.contactTypes || []).includes(type)}
                      onChange={() => handleEditLogContactTypeToggle(type)}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="editNotes">Summary Notes *</label>
              <textarea
                id="editNotes"
                value={editLogForm.summaryNotes || ''}
                onChange={(e) =>
                  setEditLogForm((prev) => ({ ...prev, summaryNotes: e.target.value }))
                }
                rows={4}
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="editFollowUp">Next Follow-Up Date</label>
              <input
                type="date"
                id="editFollowUp"
                value={editLogForm.nextFollowUpDate || ''}
                onChange={(e) =>
                  setEditLogForm((prev) => ({ ...prev, nextFollowUpDate: e.target.value }))
                }
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <input
                  type="checkbox"
                  checked={editLogForm.isRedFlagged || false}
                  onChange={(e) =>
                    setEditLogForm((prev) => ({ ...prev, isRedFlagged: e.target.checked }))
                  }
                />
                Red Flag
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <input
                  type="checkbox"
                  checked={editLogForm.isDischarge || false}
                  onChange={(e) =>
                    setEditLogForm((prev) => ({ ...prev, isDischarge: e.target.checked }))
                  }
                />
                Discharge
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <input
                  type="checkbox"
                  checked={editLogForm.isCaseReopen || false}
                  onChange={(e) =>
                    setEditLogForm((prev) => ({ ...prev, isCaseReopen: e.target.checked }))
                  }
                />
                Case Reopen
              </label>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="button-secondary"
                onClick={handleCancelEditLog}
                disabled={updateLogMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-primary"
                onClick={handleSaveLog}
                disabled={updateLogMutation.isPending}
              >
                {updateLogMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deletingLogId !== null && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setDeletingLogId(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              width: '90%',
              maxWidth: '400px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Delete Service Visit</h3>
            <p>Are you sure you want to delete this contact log? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="button-secondary"
                onClick={() => setDeletingLogId(null)}
                disabled={deleteLogMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteLog(deletingLogId)}
                disabled={deleteLogMutation.isPending}
                style={{
                  backgroundColor: '#F44336',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {deleteLogMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="card">
        <h3>Actions</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          {isEditing ? (
            <>
              <button
                className="button-primary"
                onClick={handleSaveEdit}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                className="button-secondary"
                onClick={handleCancelEdit}
                disabled={updateMutation.isPending}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <Link to="/clients">
                <button className="button-secondary">Back to Client List</button>
              </Link>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    setDeactivateError('');
                    setShowDeactivateConfirm(true);
                  }}
                  style={{
                    backgroundColor: '#F44336',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Deactivate Client
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Deactivate Client Confirmation Dialog */}
      {showDeactivateConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowDeactivateConfirm(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              width: '90%',
              maxWidth: '450px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, color: '#F44336' }}>Deactivate Client</h3>
            <p>
              Are you sure you want to deactivate{' '}
              <strong>
                {individual.firstName} {individual.lastName}
              </strong>{' '}
              ({individual.clientID})? The client will be marked as inactive and will no longer
              appear in the active client list.
            </p>
            {deactivateError && (
              <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                {deactivateError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="button-secondary"
                onClick={() => setShowDeactivateConfirm(false)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeactivateClient}
                disabled={deleteMutation.isPending}
                style={{
                  backgroundColor: '#F44336',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {deleteMutation.isPending ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FNSP Eligibility Dialog */}
      <FNSPEligibilityDialog
        individualId={individualId || 0}
        clientName={`${individual.firstName} ${individual.lastName}`}
        zipCode={individual.zipCode ?? null}
        isOpen={fnspDialogOpen}
        onClose={() => setFnspDialogOpen(false)}
        onSaved={handleFNSPDialogSaved}
      />

      {showMergePicker && individual.individualID && (
        <ManualMergePicker
          kind="client"
          anchor={{
            individualID: individual.individualID,
            clientID: individual.clientID || null,
            firstName: individual.firstName || null,
            lastName: individual.lastName || null,
            dateOfBirth: individual.dateOfBirth || null,
            gender: individual.gender || null,
            raceEthnicity: individual.raceEthnicity || null,
            address: individual.address || null,
            city: individual.city || null,
            zipCode: individual.zipCode || null,
            phoneNumber: individual.phoneNumber || null,
            emailAddress: individual.emailAddress || null,
            clientType: individual.clientType || null,
            isActive: individual.isActive ?? true,
          }}
          onCancel={() => setShowMergePicker(false)}
          onContinue={(group) => {
            setShowMergePicker(false);
            setMergeGroup(group as DuplicateGroup<DuplicateIndividual>);
          }}
        />
      )}

      {mergeGroup && (
        <MergeDialog
          kind="client"
          group={mergeGroup}
          onClose={() => setMergeGroup(null)}
          onComplete={() => {
            setMergeGroup(null);
            // Reload the page so the (possibly updated) winner reflects the merge
            // and the merged-record banner shows up correctly.
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
